// src/app/components/admin/measurement-defaults-setting.tsx
//
// Platform section of Global Settings: which model version — or, for
// Perplexity, which Agent preset (Fast / Low / Medium / High / Extra High) — a
// NEW client's first read of each lane runs.
//
// It only ever moves new clients. That read's versions are pinned onto the
// client, so its later reads reproduce it whatever this is changed to, and a
// client with a series already collected never reads this at all. The Sunbit
// Read 5 rerun is why: moving Perplexity from Low to Fast alone swung the lane
// 16 points, and a swing nobody chose cannot be read. Moving an existing client
// is a rebaseline, done per run from the Stage 4 picker.

import { useEffect, useState } from "react";
import { Check, Loader2, RotateCcw } from "lucide-react";

import {
  measurementDefaultsApi,
  type MeasurementDefaultLane,
  type MeasurementDefaultsSettings,
} from "@/api/measurement-defaults";

const muted = { fontSize: "var(--text-xs)", color: "var(--muted-foreground)", lineHeight: 1.5 } as const;

const SOURCE_LABEL: Record<MeasurementDefaultLane["source"], string> = {
  admin: "Set here",
  built_in: "Built-in default",
  catalog: "Follows the catalog's Current model",
};

export function MeasurementDefaultsSetting() {
  const [settings, setSettings] = useState<MeasurementDefaultsSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingLane, setSavingLane] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const [savedLane, setSavedLane] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    measurementDefaultsApi
      .get()
      .then((next) => alive && setSettings(next))
      .catch((e) => alive && setErr(String(e?.message || e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  async function save(lane: string, value: string | null) {
    if (savingLane) return;
    setSavingLane(lane);
    setErr("");
    setSavedLane(null);
    try {
      setSettings(await measurementDefaultsApi.update({ [lane]: value }));
      setSavedLane(lane);
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setSavingLane(null);
    }
  }

  return (
    <div className="py-6">
      <div className="max-w-2xl">
        <h2
          style={{
            fontSize: "var(--text-lg)",
            fontWeight: "var(--font-weight-semibold)",
            color: "var(--foreground)",
          }}
        >
          Measurement defaults for new clients
        </h2>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: 4 }}>
          The model each lane runs on a new client's first read. That read's choices are pinned to
          the client, so later reads repeat them. Changing a value here never moves a client that
          has already been read. To move an existing client, rebaseline it and pick the version in
          Stage 4.
        </p>

        {err && (
          <div
            className="mt-4 rounded-lg px-3 py-2"
            style={{ fontSize: "var(--text-sm)", color: "var(--error-text)", backgroundColor: "var(--error-bg)" }}
          >
            {err}
          </div>
        )}

        {loading && (
          <p className="mt-5 inline-flex items-center gap-2" style={muted}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </p>
        )}

        <div className="mt-5 flex flex-col gap-4">
          {settings?.lanes.map((lane) => {
            const busy = savingLane === lane.lane;
            const disabled = !!savingLane;
            return (
              <div key={lane.lane} className="rounded-xl border p-4" style={{ borderColor: "var(--border)" }}>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    style={{
                      fontSize: "var(--text-sm)",
                      fontWeight: "var(--font-weight-semibold)",
                      color: "var(--foreground)",
                      minWidth: 140,
                    }}
                  >
                    {lane.label}
                  </span>
                  <span style={muted}>
                    {lane.kind === "preset" ? "Agent preset" : "Model"} · {SOURCE_LABEL[lane.source]}
                  </span>
                  {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {savedLane === lane.lane && !busy && (
                    <span className="inline-flex items-center gap-1" style={{ ...muted, color: "var(--alpha-teal)" }}>
                      <Check className="h-3.5 w-3.5" /> Saved
                    </span>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {lane.choices.map((choice) => {
                    const isCurrent = choice.value === lane.value;
                    return (
                      <button
                        key={choice.value}
                        type="button"
                        disabled={disabled || isCurrent}
                        onClick={() => save(lane.lane, choice.value)}
                        className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 transition-all"
                        style={{
                          cursor: disabled || isCurrent ? "default" : "pointer",
                          fontSize: "var(--text-sm)",
                          fontWeight: "var(--font-weight-medium)",
                          borderColor: isCurrent ? "var(--alpha-teal)" : "var(--border)",
                          backgroundColor: isCurrent ? "var(--alpha-teal-10)" : "var(--card, transparent)",
                          color: isCurrent ? "var(--alpha-teal)" : "var(--foreground)",
                          boxShadow: isCurrent ? "0 0 0 1px var(--alpha-teal)" : "none",
                        }}
                      >
                        {isCurrent && <Check className="h-3.5 w-3.5" />}
                        {choice.label}
                      </button>
                    );
                  })}

                  {lane.source === "admin" && (
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => save(lane.lane, null)}
                      className="inline-flex items-center gap-1.5 rounded-lg px-2 py-2"
                      style={{
                        cursor: disabled ? "default" : "pointer",
                        fontSize: "var(--text-xs)",
                        color: "var(--muted-foreground)",
                        background: "transparent",
                      }}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Reset{lane.reset_value ? ` to ${lane.reset_value}` : ""}
                    </button>
                  )}
                </div>

                {lane.lane === "perplexity" && (
                  <p className="mt-2" style={muted}>
                    Low searches fresh on every rep (about 31 sources per answer), which matches the
                    Sonar series. Fast runs one 10-link search and every rep reuses it, so a read
                    measures 25 retrievals instead of 250. Clients already on Fast stay on Fast
                    until they are rebaselined.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default MeasurementDefaultsSetting;
